import { PUBLIC_HOSTS } from '../constant'
import type { HOSTKEY } from '../constant'
import * as utils from '../util/hostUtil'
import { createPublicAddresses, getPublicHostKey, AddressesInterface } from './public'
import { getPrivateHostKey, isPrivate } from './private'
import { KD_CONFIG_ADDRESS_KEY, KD_CONFIG_KEY } from '../constant/config'
import { isClient } from '../util'

interface ServiceOptions {
  // 自定义addresses
  addresses?: AddressesInterface
}

type HostToKeyFn = typeof getPrivateHostKey| typeof getPublicHostKey

export class Service {
  // 标记为私有化
  public isOem = false
  public getHostKey: HostToKeyFn
  public addresses: AddressesInterface = {}
  public PUBLIC_HOSTS = PUBLIC_HOSTS

  constructor (options: ServiceOptions = {}) {
    // 根据环境决定 hostToKey 函数
    this.getHostKey = isPrivate() ? getPrivateHostKey : getPublicHostKey
    // 初始化逻辑
    this.init(options)
  }

  init (options: ServiceOptions = {})  {
    // 设置addresses
    if (options.addresses) {
      this.addresses = options.addresses
    }
  }

  // 包装成 utils 导出的getHost,getScheme,getPath,getPort,getOrigin需要的参数结构
  get conf () {
    return {
      [KD_CONFIG_KEY]: {
        [KD_CONFIG_ADDRESS_KEY]: this.addresses,
      },
    }
  }

  getHost (hostKey: HOSTKEY | Lowercase<HOSTKEY>) {
    const key = this.getHostKey(hostKey)
    return utils.getHost.call(this.conf, key)
  }

  getScheme (hostKey: HOSTKEY | Lowercase<HOSTKEY>): 'https' | 'http' {
    const key = this.getHostKey(hostKey)
    return utils.getScheme.call(this.conf, key) as 'https' | 'http'
  }
  
  getPath (hostKey: HOSTKEY | Lowercase<HOSTKEY>) {
    const key = this.getHostKey(hostKey)
    return utils.getPath.call(this.conf, key)
  }
  
  getPort (hostKey: HOSTKEY | Lowercase<HOSTKEY>) {
    const key = this.getHostKey(hostKey)
    return utils.getPort.call(this.conf, key)
  }

  getOrigin (hostKey: HOSTKEY | Lowercase<HOSTKEY>) {
    const key = this.getHostKey(hostKey)
    return utils.getOrigin.call(this.conf, key)
  }

  getKdocsPath () {
    return this.getPath('KDOCS')
  }

  getServiceConf (hostKey: HOSTKEY | Lowercase<HOSTKEY>) {
    const key = this.getHostKey(hostKey)
    const target = isClient() ? window : global
    const addresses = this.conf[KD_CONFIG_KEY][KD_CONFIG_ADDRESS_KEY] || target[KD_CONFIG_KEY][KD_CONFIG_ADDRESS_KEY]
    // 兼容大小写
    const item = addresses[key.toLowerCase()] || addresses[key.toUpperCase()]
    return {
      host: item.host,
      path: item.path,
      scheme: item.scheme + ':',
      port: (Number(item.port) === 80 || Number(item.port) === 443 || !item.port) ? '' : (':' + item.port),
    }
  }

  /**
   * 替换如下场景的url
   * -    window.open
   * -    location.href
   * -    window.top.location.href
   * @param {string} url
   * @returns
   */
  replaceOpenHref (path: string = '') {
    if (isAbsoluteURL(path)) {
      return path
    } if (isPrivate()) {
      // 不在address中记录的path，都认为是kdocs下的路径
      const isKdocsPage = Object.values(this.addresses).every((item: any) => (path.indexOf(item.path) === -1))
      return isKdocsPage ? `${this.getKdocsPath()}${path}` : path
    } else {
      return path
    }
  }

  /**
   * 替换location.host
   * @param {*} path
   * @returns
   */
  replaceLocHost (path: string = '') {
    if (isClient()) {
      if (isPrivate()) {
        return `${window.location.protocol}//${window.location.host + this.getKdocsPath() + path}`
      } else  {
        return `${window.location.protocol}//${window.location.host + path}`
      }
    } else {
      return path
    }
  }
}

declare global {
  interface Window {
    kdService: {}
  }
}

function getAddresses (options: ServiceOptions = {}) {
  let addresses
  if (options.addresses) {
    // 1. 取 options.addresses
    addresses = options.addresses
  } else {
    // 2. 取全局的配置，兼容旧写法和私有化环境的配置
    addresses = isClient() ? (window[KD_CONFIG_KEY] && window[KD_CONFIG_KEY][KD_CONFIG_ADDRESS_KEY]) : (global[KD_CONFIG_KEY] && global[KD_CONFIG_KEY][KD_CONFIG_ADDRESS_KEY])
  } 

  // 3. 以上两种情况仍然取不到
  if (!addresses) {
    if (isPrivate()) {
      // a. 私有化环境抛错
      console.log('私网环境 addresses 为必要参数')
      throw new Error('私网环境 addresses 为必要参数')
    } else {
      // b. 公网根据PUBLIC_HOSTS配置自动创建addresses
      addresses = createPublicAddresses()
    }
  }

  return addresses
}

export function isAbsoluteURL(url) {
  return /^([a-z][a-z\d\+\-\.]*:)?\/\//i.test(url);
};

export function createService (options: ServiceOptions = {}) {
  // 检查并获取 addresses 配置
  const addresses = getAddresses(options)
  return new Service(addresses ? { ...options, addresses } : options)
}
